import PropTypes from 'prop-types';
import React, { useState, useEffect, Fragment, useReducer, useCallback } from 'react';
import { Row, Col, Icon, Tooltip, Modal, Input, Popconfirm } from 'antd';
import { Field, FieldArray } from 'redux-form';
import { SectionTitle } from '../../styles/BasicStyles';
import { DeleteSeatButton, SeatAge, SeatFoodRestrict, SeatName, SeatNotes, SeatRow, TableSeatCode } from './RoomPlanStyles';
import Alerts from '../../components/alert/Alert';
import SelectAgeInput from '../../components/inputs/SelectAgeInput';
import SelectRestrictionInput from '../../components/inputs/SelectRestrictionInput';
import { MaterialInput } from '../../components/inputs/MaterialStyles';
import { AgeTypes, GuestAgeEnum, TablePurposeEnum, setRestrictionsBasedOnAge } from './utilGuest';

const REQUIRED_RESTRICTIONS = [
  '6576f9f0e5c2e224a81aeaa1',  // VEGETARIAN
  '6576f9f0e5c2e224a81aeaa2',  // VEGAN
  '6576f9f1e5c2e224a81aeab6',  // CHILD MENU
  '67c03d27b272a8dfe4179381',  // FISH
  '67c03cceb272a8dfe4179380',  // MEAT
  '6800ca616e17ee30047f1407'   // NEWBORN
];

const GuestSeatComponent = ({
  wedding,
  validated,
  roomPlanClosed,
  index,
  seat,
  table,
  foodRestrictions,
  parentUpdateSeat,
  parentRemoveSeat,
  translate,
  dishes,
  isBlockedOptionActivated
}) => {
  const [tableObj, setTable] = useState(table);

  const [number, setNumber] = useState(seat?.number);
  const [guestName, setGuestName] = useState(seat?.guestName);
  const [guestNotes, setGuestNotes] = useState(seat?.guestNotes);
  const [guestAge, setGuestAge] = useState(seat?.guestAge);
  const [guestRestrictions, setGuestRestrictions] = useState(seat?.foodRestrictions);
  const [isCouple, setIsCouple] = useState(seat.couple);
  const [frontCouple, setFrontCouple] = useState(seat.frontCouple);
  const [isBlocked, setIsBlocked] = useState(seat?.isBlocked || false);

  const [disabled, setDisabled] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [isValidated, setValidated] = useState(validated);
  const [isRoomPlanClosed, setRoomPlanClosed] = useState(roomPlanClosed);

  const [restrictionError, setRestrictionError] = useState(false);

  const [otherRestrictionSelected, setOtherRestrictionSelected] = useState(false);

  const BlockSeatMessage = (
    <div>
      <div>{translate('CONFIRM_BLOCK_PLACE')} </div>
      <div>
        {translate('NOTES_BLOCK_PLACE')}
      </div>
    </div>
  );

  // useEffect(() => {
  //   if (wedding?.upgrade?.tag === 'TREND') {
  //     if (!disabled && guestName?.trim()) {
  //       const isValid = hasRequiredRestriction(seat?.foodRestrictions);
  //       setRestrictionError(!isValid);
  //     } else {
  //       setRestrictionError(false);
  //     }
  //   }
  // }, [seat?.foodRestrictions, guestName, disabled]);

  useEffect(() => {
    setNumber(seat?.number);
    setGuestName(seat?.guestName);
    setGuestAge(seat?.guestAge);
    setGuestRestrictions(reorderFoodRestrictions(seat?.foodRestrictions));
    setGuestNotes(seat?.guestNotes);
    setIsCouple(seat?.couple);
    setFrontCouple(seat?.frontCouple);
    setIsBlocked(seat?.isBlocked || false);
    checkIfNotesRequiredRestrictionIsSelected();
  }, [seat, seat?.guestName, seat?.guestAge, seat?.couple, seat?.frontCouple, seat?.foodRestrictions, seat?.isBlocked]);

  useEffect(() => {
    setTable(table);
    setDisabled(!table?.active);
    setIsStaff(table?.tablePurpose == TablePurposeEnum.STAFF);
    checkIfNotesRequiredRestrictionIsSelected();
  }, [table]);

  useEffect(() => {
    setValidated(validated);
  }, [validated]);

  useEffect(() => {
    setRoomPlanClosed(roomPlanClosed);
  }, [roomPlanClosed]);

  const getSeatCode = () => {
    return (number || 0) + 1;
  }

  const blockSeat = () => {
    if (!isBlockedOptionActivated) {
      return;
    }

    if (isValidated || isRoomPlanClosed || disabled || (!tableObj?.seatsFrontCouple && seat?.frontCouple)) {
      return;
    }

    setGuestName('');
    setGuestAge(GuestAgeEnum.ADULT);
    setGuestRestrictions([]);
    setGuestNotes('');
    setIsBlocked(!isBlocked);
    parentUpdateSeat('UPDATE_SEAT', index, {
      number,
      couple: isCouple,
      frontCouple,
      guestName: '',
      guestAge: GuestAgeEnum.ADULT,
      foodRestrictions: [],
      isBlocked: !isBlocked,
      guestNotes: ''
    });

    checkIfNotesRequiredRestrictionIsSelected();
  }


  const removeSeat = () => {
    if (isValidated || isRoomPlanClosed || disabled || (!tableObj?.seatsFrontCouple && seat?.frontCouple)) {
      return;
    }

    if (isStaff) {
      parentRemoveSeat(index);
    } else {
      setGuestName('');
      setGuestAge(GuestAgeEnum.ADULT);
      setGuestRestrictions([]);
      setGuestNotes('');
      setIsBlocked(false);
      parentUpdateSeat('UPDATE_SEAT', index, {
        number,
        couple: isCouple,
        frontCouple,
        guestName: '',
        guestAge: GuestAgeEnum.ADULT,
        foodRestrictions: [],
        isBlocked: false,
        guestNotes: ''
      });
    }

    checkIfNotesRequiredRestrictionIsSelected();
  }

  const sanitizeGuestName = (str) => {
    return str
      .replace(/[\x00-\x1F\x7F]/g, '')
      .replace(/\u200B/g, '')
    // .replace(/[^\p{L}\p{M}'’\- ]+/gu, '');
  }

  const sanitizeGuestNotes = (str) => {
    return str
      .replace(/[\x00-\x1F\x7F]/g, '')
      .replace(/\u200B/g, '')
    // .replace(/[^\p{L}\p{M}'’\- ]+/gu, '');
  }

  const handleNameChange = (event) => {
    const cleaned = sanitizeGuestName(event?.target?.value);
    if (isValidated || isRoomPlanClosed || disabled || (!tableObj?.seatsFrontCouple && seat?.frontCouple)) {
      return;
    }

    selectedMainDish(guestRestrictions);
    setGuestName(cleaned);
    if (!cleaned || cleaned?.trim() === '') {
      setGuestNotes(null);
      setGuestRestrictions([]);
    }

    parentUpdateSeat('UPDATE_SEAT', index, {
      number,
      couple: isCouple,
      frontCouple,
      guestName: cleaned,
      guestAge,
      foodRestrictions: (!cleaned || cleaned?.trim() === '') ? [] : guestRestrictions,
      guestNotes: (!cleaned || cleaned?.trim() === '') ? '' : guestNotes
    });
  };

  // sanitização total no blur
  const handleNameBlur = () => {
    setGuestName(prev => prev?.trim());
  };

  const handleGuestNotesChange = (event) => {
    const cleaned = sanitizeGuestNotes(event?.target?.value);
    if (isValidated || isRoomPlanClosed || disabled || (!tableObj?.seatsFrontCouple && seat?.frontCouple)) {
      return;
    }

    selectedMainDish(guestRestrictions);
    setGuestNotes(cleaned);
    parentUpdateSeat('UPDATE_SEAT', index, {
      number,
      couple: isCouple,
      frontCouple,
      guestName,
      guestAge,
      foodRestrictions: guestRestrictions,
      guestNotes: cleaned
    });
  };

  // sanitização total no blur
  const handleGuestNotesBlur = () => {
    setGuestNotes(prev => prev?.trim());
  };

  const handleAgeChange = (value) => {
    if (isValidated || isRoomPlanClosed || disabled || (!tableObj?.seatsFrontCouple && seat?.frontCouple)) {
      return;
    }

    const originalGuestAge = guestAge;
    const restrictionsToUpdate = setRestrictionsBasedOnAge(originalGuestAge, value, guestRestrictions, foodRestrictions);
    setGuestAge(value);
    setGuestRestrictions(restrictionsToUpdate);

    selectNewbornOptionOnRestrictions(value, restrictionsToUpdate);

    parentUpdateSeat('UPDATE_SEAT', index, {
      number,
      couple: isCouple,
      frontCouple,
      guestName,
      guestAge: value,
      foodRestrictions: restrictionsToUpdate,
      guestNotes
    });

    checkIfNotesRequiredRestrictionIsSelected();
  }

  const handleChangeRestriction = (values) => {
    if (isValidated || isRoomPlanClosed || disabled || (!tableObj?.seatsFrontCouple && seat?.frontCouple)) {
      return;
    }

    // if (wedding?.upgrade?.tag === 'TREND') {
    //   const isValid = hasRequiredRestriction(values);
    //   setRestrictionError(!isValid);
    // }

    setGuestRestrictions(values);
    parentUpdateSeat('UPDATE_SEAT', index, {
      number,
      couple: isCouple,
      frontCouple,
      guestName,
      guestAge,
      foodRestrictions: values,
      guestNotes
    });

    checkIfNotesRequiredRestrictionIsSelected();
  }

  const checkIfNotesRequiredRestrictionIsSelected = () => {
    const hasNotesRequired = guestRestrictions.some(rId =>
      foodRestrictions.find(fr => fr._id === rId && fr.notesRequired)
    );
    if (hasNotesRequired) {
      setOtherRestrictionSelected(true);
    } else {
      setOtherRestrictionSelected(false);
      setGuestNotes(null);
    }
  }

  const showAlert = () => {
    if (isValidated) {
      return Alerts.new({
        type: "success",
        title: translate('BLOCKED_ANSWER_TITLE'),
        text: translate('ROOM_PLAN_APPROVED')
      });
    }
    else if (isRoomPlanClosed) {
      return Alerts.new({
        type: "success",
        title: translate('BLOCKED_ANSWER_TITLE'),
        // text: translate('BLOCKED_ANSWER_TEXT')
      });
    }
  }

  const reorderFoodRestrictions = (foodRestrictions) => {
    const upgradeSelections = foodRestrictions.filter(f => f?.upgradeSelection);
    const nonUpgradeSelections = foodRestrictions.filter(f => !f?.upgradeSelection);

    if (wedding?.upgrade?.tag === 'TREND') {
      const RESTRICTIONS_TO_REMOVE = [
        '6576f9f0e5c2e224a81aeaa4',
        '6576f9f0e5c2e224a81aeaa3',
        '6576f9f0e5c2e224a81aeaa7',
        '6576f9f0e5c2e224a81aeaa8'
      ];

      let selections = [...upgradeSelections, ...nonUpgradeSelections];
      for (const r of RESTRICTIONS_TO_REMOVE) {
        const rIdx = selections.findIndex(s => s?._id === r);
        if (rIdx !== -1) {
          selections.splice(rIdx, 1);
        }
      }

      return selections;
    }

    return nonUpgradeSelections;
  }

  const blockBothMainDishesForFoodRestrictions = (guestAge) => {
    if (wedding?.upgrade?.tag === 'TREND') {
      if (guestAge && guestAge === 'NEWBORN') {
        return true;
      }

      if (
        (dishes?.meatAnswer && !dishes?.fishAnswer) ||
        (!dishes?.meatAnswer && dishes?.fishAnswer) ||
        (!dishes?.meatAnswer && !dishes?.fishAnswer)
      ) {
        return true;
      }
    }

    return false;
  }

  const selectNewbornOptionOnRestrictions = (guestAge, restrictions) => {
    if (wedding?.upgrade?.tag === 'TREND' && guestAge === 'NEWBORN') {
      const MEAT_RESTRICTION_ID = '67c03cceb272a8dfe4179380';
      const FISH_RESTRICTION_ID = '67c03d27b272a8dfe4179381';
      const NEWBORN_RESTRICTION_ID = '6800ca616e17ee30047f1407';

      const alreadyMeatIdx = restrictions.findIndex(r => r === MEAT_RESTRICTION_ID);
      if (alreadyMeatIdx !== -1) {
        restrictions.splice(alreadyMeatIdx, 1);
      }

      const alreadyFishIdx = restrictions.findIndex(r => r === FISH_RESTRICTION_ID);
      if (alreadyFishIdx !== -1) {
        restrictions.splice(alreadyFishIdx, 1);
      }

      const alreadyNewbornIdx = restrictions.findIndex(r => r === NEWBORN_RESTRICTION_ID);
      if (alreadyNewbornIdx === -1) {
        restrictions.push(NEWBORN_RESTRICTION_ID);
      }

      setGuestRestrictions(restrictions);
      checkIfNotesRequiredRestrictionIsSelected();
    }
  }

  const selectedMainDish = (restrictions) => {
    if (wedding?.upgrade?.tag === 'TREND') {
      const MEAT_RESTRICTION_ID = '67c03cceb272a8dfe4179380';
      const FISH_RESTRICTION_ID = '67c03d27b272a8dfe4179381';

      if (dishes?.meatAnswer && !dishes?.fishAnswer) {
        const alreadyMeat = restrictions.find(r => r === MEAT_RESTRICTION_ID);
        if (!alreadyMeat) {
          restrictions.push(MEAT_RESTRICTION_ID);
          setGuestRestrictions(restrictions);
        }
      } else if (!dishes?.meatAnswer && dishes?.fishAnswer) {
        const alreadyFish = restrictions.find(r => r === FISH_RESTRICTION_ID);
        if (!alreadyFish) {
          restrictions.push(FISH_RESTRICTION_ID);
          setGuestRestrictions(restrictions);
        }
      }
    }

    checkIfNotesRequiredRestrictionIsSelected();
    return restrictions;
  }

  const hasRequiredRestriction = (restrictions) => {
    return REQUIRED_RESTRICTIONS.some(req => restrictions?.includes(req));
  };

  const restrictionsColors = (restrictions) => {
    const MEAT_COLOR = '#d6f5d6';
    const FISH_COLOR = '#cce0ff';
    const VEGETARIAN_COLOR = '#ffebcc';
    const VEGAN_COLOR = '#ecd8c6';
    const CHILDMENU_COLOR = '#ffccf2';
    const NEWBORN_COLOR = '#ffccf2';

    let colors = [];
    if (wedding?.upgrade?.tag === 'TREND') {
      for (const r1 of REQUIRED_RESTRICTIONS) {
        const mainDishSelected = restrictions.find(r2 => r2 === r1);
        if (mainDishSelected) {
          switch (r1) {
            case '67c03cceb272a8dfe4179380':
              colors.push(MEAT_COLOR);
              break;
            case '67c03d27b272a8dfe4179381':
              colors.push(FISH_COLOR);
              break;
            case '6576f9f0e5c2e224a81aeaa1':
              colors.push(VEGETARIAN_COLOR);
              break;
            case '6576f9f0e5c2e224a81aeaa2':
              colors.push(VEGAN_COLOR);
              break;
            case '6576f9f1e5c2e224a81aeab6':
              colors.push(CHILDMENU_COLOR);
              break;
            case '6800ca616e17ee30047f1407':
              colors.push(NEWBORN_COLOR);
              break;
          }
        }
      }
    }

    return colors;
  }

  return (
    <SeatRow key={index} disabled={disabled} hasError={restrictionError} isBlocked={isBlocked}>
      <TableSeatCode seat={true} couple={isCouple}>
        {getSeatCode() || 'X'}
      </TableSeatCode>

      <SeatName>
        <MaterialInput
          type="text"
          translate="no"
          placeholder={translate('NAME')}
          noLabel={true}
          readOnly={isValidated || isRoomPlanClosed || disabled || (!tableObj?.seatsFrontCouple && seat?.frontCouple) || isBlocked}
          disabled={isBlocked}
          value={guestName}
          onChange={handleNameChange}
          onBlur={handleNameBlur}
          onClick={() => showAlert()} />
      </SeatName>
      <SeatAge staff={isStaff}>
        <SelectAgeInput
          placeholder={translate('AGE')}
          meta={{ invalid: false, submitFailed: false }}
          data={AgeTypes}
          readOnly={isValidated || isRoomPlanClosed || disabled || (!tableObj?.seatsFrontCouple && seat?.frontCouple) || isBlocked}
          disabled={isBlocked}
          translatable={true}
          translate={translate}
          input={{
            value: guestAge,
            onChange: handleAgeChange,
            onFocus: () => showAlert()
          }} />
      </SeatAge>
      <SeatFoodRestrict staff={isStaff}>
        <SelectRestrictionInput
          placeholder={translate('ALLERGIAS')}
          meta={{ invalid: false, submitFailed: false }}
          readOnly={isValidated || isRoomPlanClosed || disabled || (!tableObj?.seatsFrontCouple && seat?.frontCouple) || isBlocked}
          disabled={isBlocked}
          blockMainDishes={blockBothMainDishesForFoodRestrictions(guestAge)}
          data={reorderFoodRestrictions(foodRestrictions)}
          restrictionsColors={restrictionsColors(guestRestrictions)}
          input={{
            value: guestRestrictions,
            onChange: handleChangeRestriction,
            onFocus: () => showAlert()
          }}
        />
      </SeatFoodRestrict>
      <SeatNotes hasError={guestRestrictions?.some(rId => foodRestrictions.find(fr => fr._id === rId && fr.notesRequired)) && (!guestNotes || guestNotes?.trim() === '') && guestName?.trim() !== ''}>
        <Input
          type="text"
          translate="no"
          style={{ color: guestNotes?.trim() !== '' ? 'black' : 'inherit' }}
          disabled={disabled || (!tableObj?.seatsFrontCouple && seat?.frontCouple) || isBlocked || !otherRestrictionSelected || !guestName || guestName?.trim() === ''}
          value={guestNotes}
          placeholder={translate('NOTES')}
          onChange={handleGuestNotesChange}
          onBlur={handleGuestNotesBlur}
        />
      </SeatNotes>
      <DeleteSeatButton
        readOnly={isValidated || isRoomPlanClosed || disabled || (!tableObj?.seatsFrontCouple && seat?.frontCouple)}
        onClick={removeSeat}>
        <Icon type="delete" />
      </DeleteSeatButton>
      {isBlockedOptionActivated && <DeleteSeatButton
        readOnly={isValidated || isRoomPlanClosed || disabled || (!tableObj?.seatsFrontCouple && seat?.frontCouple)}
        onClick={e => (seat?.guestName && seat?.guestName !== '') ? e.stopPropagation() : blockSeat()}>
        {(seat?.guestName && seat?.guestName !== '') && <Popconfirm
          placement="topRight"
          title={BlockSeatMessage}
          okText={translate('BLOCK')}
          onConfirm={blockSeat}>
          <Icon type={"stop"} title={translate('EMPTY_PLACE')} />
        </Popconfirm>}
        {(!seat?.guestName || seat?.guestName === '') && <Icon type={"stop"} title={"Lugar vazio"} />}
      </DeleteSeatButton>}
    </SeatRow>
  );
}
export default GuestSeatComponent;